import React, { useEffect, useMemo, useState } from "react";
import { X } from "lucide-react";
import { apiService } from "../../services/apiService";
import {
  computeInvoiceProgressSummary,
  formatMonthLongYm,
  getCurrentInvoiceMonth,
} from "../../utils/invoiceMonthDisplay";

import CustomSelect from "../ui/CustomSelect";
const MONTH_SHORT = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

function ym(year, month) {
  return `${year}-${String(month).padStart(2, "0")}`;
}

function monthStatus({ year, month, closedSet, currentYm, progress }) {
  const thisYm = ym(year, month);
  if (thisYm > currentYm) {
    return { kind: "not_started", closed: false };
  }
  const closed = closedSet.has(month);
  if (closed) return { kind: "closed", closed: true, progress };
  return { kind: "open", closed: false, progress };
}

const ReportingMonthsModal = ({
  open,
  onClose,
  tenantId,
  canEdit = true,
  initialYear,
  onSaved,
  setToast,
}) => {
  const currentYm = getCurrentInvoiceMonth();
  const currentYear = Number(currentYm.slice(0, 4));
  const [year, setYear] = useState(initialYear || currentYear);
  const [draftClosed, setDraftClosed] = useState([]);
  const [monthProgress, setMonthProgress] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const yearOptions = useMemo(() => {
    const years = [];
    for (let y = currentYear - 2; y <= currentYear + 1; y += 1) years.push(y);
    return years;
  }, [currentYear]);

  useEffect(() => {
    if (open) setYear(initialYear || currentYear);
  }, [open, initialYear, currentYear]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);
    const params = new URLSearchParams({ year: String(year) });
    if (tenantId != null && tenantId !== "") {
      params.set("tenant_id", String(tenantId));
    }
    const closedReq = apiService.request(
      `/analytics/closed-months?${params.toString()}`,
    );
    const progressReqs = Array.from({ length: 12 }, (_, i) => {
      const month = i + 1;
      const thisYm = ym(year, month);
      if (thisYm > currentYm) {
        return Promise.resolve({ month, progress: null });
      }
      return apiService
        .listInvoices({ invoiceMonth: thisYm })
        .then((res) => ({
          month,
          progress: computeInvoiceProgressSummary(
            Array.isArray(res?.data) ? res.data : [],
          ),
        }))
        .catch(() => ({ month, progress: null }));
    });

    Promise.all([closedReq, ...progressReqs])
      .then(([closedRes, ...progressRows]) => {
        if (cancelled) return;
        setDraftClosed(
          (closedRes?.closed_months || [])
            .map(Number)
            .filter((m) => m >= 1 && m <= 12),
        );
        const next = {};
        progressRows.forEach(({ month, progress }) => {
          next[month] = progress;
        });
        setMonthProgress(next);
      })
      .catch(() => {
        if (!cancelled) {
          setDraftClosed([]);
          setMonthProgress({});
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, year, tenantId, currentYm]);

  const closedSet = useMemo(() => new Set(draftClosed), [draftClosed]);

  const closedThroughLabel = useMemo(() => {
    if (!draftClosed.length) return null;
    const last = Math.max(...draftClosed);
    return formatMonthLongYm(ym(year, last));
  }, [draftClosed, year]);

  const toggleMonth = (month) => {
    if (!canEdit) return;
    const thisYm = ym(year, month);
    if (thisYm > currentYm) return;
    setDraftClosed((prev) =>
      prev.includes(month)
        ? prev.filter((x) => x !== month)
        : [...prev, month].sort((a, b) => a - b),
    );
  };

  const handleSave = async () => {
    if (!canEdit || saving) return;
    setSaving(true);
    try {
      const body = {
        year,
        closed_months: draftClosed,
      };
      if (tenantId != null && tenantId !== "") {
        body.tenant_id = tenantId;
      }
      const res = await apiService.request(`/analytics/closed-months`, {
        method: "PUT",
        body: JSON.stringify(body),
      });
      const queued = Array.isArray(res?.summary_queued) ? res.summary_queued : [];
      setToast?.({
        message:
          queued.length > 0
            ? `Reporting months saved. Monthly note generating for ${queued.length} month(s).`
            : "Reporting months saved",
        type: "success",
      });
      onSaved?.(draftClosed, year);
      onClose?.();
    } catch (err) {
      setToast?.({
        message: err.message || "Failed to save reporting months.",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-5 bg-[#1c1c1a]/25 backdrop-blur-[4px]"
      style={{ WebkitBackdropFilter: "blur(4px)" }}
      role="presentation"
      onClick={() => {
        if (!saving) onClose?.();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="reporting-months-title"
        className="w-full max-w-[860px] max-h-[90vh] flex flex-col rounded-[12px] bg-white shadow-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-5 pb-3 flex items-start justify-between gap-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.05em] text-[var(--accent,#a51c1c)]">
              Client Administration
            </p>
            <h2
              id="reporting-months-title"
              className="mt-0.5 text-[20px] font-semibold text-[var(--ink,#1c1c1a)]"
            >
              Reporting months
            </h2>
            <p className="mt-1 text-[13px] text-[var(--muted,#6b6a66)] max-w-xl">
              Closed months are final and feed the YTD and forecast figures. Open
              months show invoices received so far.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <CustomSelect
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="h-8 rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white px-2 text-[13px] text-[var(--ink,#1c1c1a)]"
            >
              {yearOptions.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </CustomSelect>
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="p-1.5 rounded-md text-[var(--muted,#6b6a66)] hover:bg-[var(--sunken,#eeece7)]"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="px-6 pb-4 overflow-y-auto flex-1">
          {loading ? (
            <p className="py-10 text-center text-[13px] text-[var(--muted,#6b6a66)]">
              Loading months…
            </p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {MONTH_SHORT.map((label, idx) => {
                const month = idx + 1;
                const progress = monthProgress[month];
                const status = monthStatus({
                  year,
                  month,
                  closedSet,
                  currentYm,
                  progress,
                });
                const sub =
                  status.kind === "not_started"
                    ? "—"
                    : status.kind === "open" && ym(year, month) === currentYm
                      ? `${progress?.received ?? 0} of ${progress?.expected ?? 0} invoices so far`
                      : `${progress?.received ?? 0} of ${progress?.expected ?? 0} invoices`;
                return (
                  <div
                    key={month}
                    className="rounded-[10px] border border-[var(--line,#e4e2dc)] bg-[var(--ground,#f5f4f1)] px-3 py-3"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-[14px] font-semibold text-[var(--ink,#1c1c1a)]">
                        {label}
                      </span>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={status.closed}
                        disabled={!canEdit || status.kind === "not_started" || saving}
                        onClick={() => toggleMonth(month)}
                        className={`relative h-5 w-9 rounded-full shrink-0 disabled:opacity-50 ${
                          status.closed
                            ? "bg-[var(--accent,#a51c1c)]"
                            : "bg-[#d5d3cc]"
                        }`}
                      >
                        <span
                          className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow ${
                            status.closed ? "right-0.5" : "left-0.5"
                          }`}
                        />
                      </button>
                    </div>
                    <div className="mt-2 flex items-center gap-1.5 text-[12px]">
                      {status.kind === "closed" ? (
                        <>
                          <span className="w-1.5 h-1.5 rounded-full bg-[#9a9a96]" />
                          <span className="text-[var(--muted,#6b6a66)]">Closed</span>
                        </>
                      ) : status.kind === "open" ? (
                        <>
                          <span className="w-1.5 h-1.5 rounded-full bg-[#3b6ea8]" />
                          <span className="text-[#3b6ea8]">Open</span>
                        </>
                      ) : (
                        <>
                          <span className="w-1.5 h-1.5 rounded-full bg-[#d5d3cc]" />
                          <span className="text-[var(--faint,#9a9a95)]">Not started</span>
                        </>
                      )}
                    </div>
                    <p className="mt-1 text-[12px] text-[var(--muted,#6b6a66)]">
                      {sub}
                    </p>
                  </div>
                );
              })}
            </div>
          )}
          <p className="mt-4 text-[12px] text-[var(--muted,#6b6a66)]">
            Closing a month locks its invoices for reporting and triggers the
            monthly note. Reopen a month to let a late invoice through, then
            close it again.
          </p>
        </div>

        <div className="px-6 py-3 border-t border-[var(--line,#e4e2dc)] bg-[var(--ground,#f5f4f1)] flex items-center justify-between gap-3">
          <p className="text-[12px] text-[var(--muted,#6b6a66)]">
            {draftClosed.length} of 12 closed
            {closedThroughLabel ? ` · closed through ${closedThroughLabel}` : ""}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 rounded-md border border-[var(--line-strong,#d5d3cc)] bg-white text-[13px] font-medium text-[var(--ink,#1c1c1a)] hover:bg-[var(--sunken,#eeece7)] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={!canEdit || saving || loading}
              className="px-4 py-2 rounded-md bg-[var(--accent,#a51c1c)] hover:bg-[#8c1a1a] text-white text-[13px] font-semibold disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save reporting months"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportingMonthsModal;
